import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useToast } from "@/components/base/Toast";
import { useStore } from "@/context/StoreContext";
import type { BranchDetail } from "@/types";
import { formatSAR } from "@/utils/format";
import BranchCard from "./components/BranchCard";

export default function BranchesPage() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { branches: list, employees, toggleBranchOpen, toggleBranchActive } =
    useStore();
  const [disableTarget, setDisableTarget] = useState<BranchDetail | null>(null);

  const enriched = useMemo(
    () =>
      list.map((branch) => {
        const staff = employees.filter(
          (employee) => employee.branchId === branch.id,
        );
        const manager = branch.managerId
          ? staff.find((employee) => employee.id === branch.managerId)
          : staff.find((employee) => employee.role === "branchManager");
        return {
          ...branch,
          staffCount: staff.length,
          manager: manager?.name ?? "",
        };
      }),
    [list, employees],
  );

  const stats = useMemo(() => {
    return {
      total: list.length,
      active: list.filter((branch) => branch.isActive).length,
      openNow: list.filter((branch) => branch.isActive && branch.isOpen).length,
      sales: list.reduce((sum, branch) => sum + branch.salesToday, 0),
    };
  }, [list]);

  const handleToggleOpen = (id: string, value: boolean) => {
    toggleBranchOpen(id, value);
    show(value ? "تم فتح الفرع لاستقبال الطلبات" : "تم إغلاق الفرع مؤقتًا");
  };

  const handleToggleActive = (id: string, value: boolean) => {
    if (!value) {
      const target = list.find((branch) => branch.id === id) ?? null;
      setDisableTarget(target);
      return;
    }
    toggleBranchActive(id, true);
    show("تم تفعيل الفرع");
  };

  const confirmDisable = () => {
    if (!disableTarget) return;
    toggleBranchActive(disableTarget.id, false);
    show("تم تعطيل الفرع وإيقاف طلباته", "warning");
    setDisableTarget(null);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          {
            label: "إجمالي الفروع",
            value: String(stats.total),
            icon: "ri-store-3-line",
            tone: "bg-primary-100 text-primary-700",
          },
          {
            label: "فروع مفعّلة",
            value: String(stats.active),
            icon: "ri-checkbox-circle-line",
            tone: "bg-secondary-200 text-secondary-800",
          },
          {
            label: "مفتوحة الآن",
            value: String(stats.openNow),
            icon: "ri-door-open-line",
            tone: "bg-accent-100 text-accent-800",
          },
          {
            label: "مبيعات الفروع اليوم",
            value: formatSAR(stats.sales),
            icon: "ri-money-dollar-circle-line",
            tone: "bg-primary-100 text-primary-700",
          },
        ].map((item) => (
          <div
            key={item.label}
            className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5"
          >
            <div className="flex items-center gap-2 text-foreground-500">
              <span
                className={`w-7 h-7 flex items-center justify-center rounded-full ${item.tone}`}
              >
                <i className={item.icon}></i>
              </span>
              <span className="text-xs">{item.label}</span>
            </div>
            <p className="mt-3 text-2xl font-bold text-foreground-950">
              {item.value}
            </p>
          </div>
        ))}
      </div>

      <section className="yc-fade-up flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-white rounded-card border border-background-200 shadow-card px-5 py-4">
        <div>
          <h2 className="text-base font-bold text-foreground-950">
            فروع المطعم
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">
            تحكّم في حالة كل فرع وأوقات العمل وبيانات المدير
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/branches/new")}
          className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-primary-500 px-5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line text-lg"></i>
          إضافة فرع
        </button>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
        {enriched.map((branch) => (
          <BranchCard
            key={branch.id}
            branch={branch}
            onToggleOpen={handleToggleOpen}
            onToggleActive={handleToggleActive}
            onEdit={(item) => navigate(`/branches/${item.id}/edit`)}
          />
        ))}
      </div>

      <ConfirmDialog
        open={disableTarget !== null}
        title="تعطيل الفرع"
        message={`سيتم إيقاف استقبال الطلبات من «${disableTarget?.name ?? ""}» حتى إعادة تفعيله. هل تريد المتابعة؟`}
        confirmLabel="تعطيل الفرع"
        tone="danger"
        onConfirm={confirmDisable}
        onCancel={() => setDisableTarget(null)}
      />
    </div>
  );
}